import { approveReplenishmentInstruments, previewReplenishmentApproval, type ApprovalGroup, type ApprovalGroupBy, type ApprovalLine, type ApprovalPreview } from '@api/replenishment-engine';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { Alert, Badge, Button, Checkbox, Group, Loader, SegmentedControl, Stack, Text } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useCallback, useEffect, useMemo, useState, type FC, type ReactElement } from 'react';

import ExplainWindow from '../ExplainWindow';
import { dayLabel, localDateKey, whenLabel } from './dates';
import { Lines } from './tables';

const n = (v: number, maxDecimals = 1): string => formatCount(v, { maxDecimals });

type Props = {
    /** The instruments asked for; the sheet shows the whole chain they bring along. */
    ids: string[] | null;
    objectiveId?: string;
    onClose: () => void;
    /** After a confirmed approval: reload whatever shows instruments. */
    onDone: () => void;
};

const KIND_LABEL: Record<ApprovalGroup['kind'], string> = { MOTHER_ORDER: 'Purchase order', PRODUCTION_ORDER: 'Production order', STN: 'Transfer' };
const KIND_DATE: Record<ApprovalGroup['kind'], string> = { MOTHER_ORDER: 'Order date', PRODUCTION_ORDER: 'Start', STN: 'Dispatch' };

const readError = (e: unknown): string => {
    const err = e as { error?: { message?: string }; message?: string };
    return err?.error?.message ?? err?.message ?? 'Something went wrong';
};

/**
 * The review before approval: every document the approval creates, with its summed lines
 * and the SKUs each line feeds. Group by day (one document per date) or by month (one
 * per vendor, factory or lane for the month, dated at its earliest place-by).
 */
const ApprovalSheet: FC<Props> = ({ ids, objectiveId, onClose, onDone }) => {
    const today = localDateKey();
    const [groupBy, setGroupBy] = useState<ApprovalGroupBy>('DAY');
    const [preview, setPreview] = useState<ApprovalPreview | null>(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<ApprovalGroup[] | null>(null);
    // Upstream documents the reviewer left out: they stay proposed, and the run they feed starts late.
    const [excluded, setExcluded] = useState<string[]>([]);
    // Lines the reviewer asked for and then unticked: they stay proposed, and nothing they bring along comes either.
    const [dropped, setDropped] = useState<ApprovalLine[]>([]);
    const asked = useMemo(() => {
        const out = new Set(dropped.flatMap((l) => l.instrumentIds));
        return ids ? ids.filter((id) => !out.has(id)) : null;
    }, [ids, dropped]);

    const load = useCallback(async () => {
        if (!asked) return;
        if (asked.length === 0) {
            setPreview({ groups: [], alreadyApproved: [], refused: [] });
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setPreview(await previewReplenishmentApproval(asked, groupBy, objectiveId, excluded));
        } catch (e) {
            setError(readError(e));
        } finally {
            setLoading(false);
        }
    }, [asked, groupBy, objectiveId, excluded]);
    useEffect(() => {
        setResult(null);
        void load();
    }, [load]);
    useEffect(() => {
        setExcluded([]);
        setDropped([]);
    }, [ids]);
    const askedSet = useMemo(() => new Set(ids ?? []), [ids]);
    const dropLine = (l: ApprovalLine): void => setDropped((cur) => [...cur.filter((x) => x.instrumentIds.join() !== l.instrumentIds.join()), l]);
    const keepLine = (l: ApprovalLine): void => setDropped((cur) => cur.filter((x) => x.instrumentIds.join() !== l.instrumentIds.join()));
    // What was left out, remembered across previews so it can be ticked back on.
    const [leftOut, setLeftOut] = useState<ApprovalGroup[]>([]);
    const leaveOut = (g: ApprovalGroup): void => {
        setLeftOut((cur) => [...cur.filter((x) => x.key !== g.key), g]);
        setExcluded((cur) => [...new Set([...cur, ...g.instrumentIds])]);
    };
    const bringBack = (g: ApprovalGroup): void => {
        setLeftOut((cur) => cur.filter((x) => x.key !== g.key));
        setExcluded((cur) => cur.filter((id) => !g.instrumentIds.includes(id)));
    };

    const confirm = async (): Promise<void> => {
        if (!ids) return;
        setBusy(true);
        setError(null);
        try {
            if (!asked || asked.length === 0) return;
            const res = await approveReplenishmentInstruments(asked, objectiveId, groupBy, excluded);
            setResult(res.documents);
            if (res.refused.length) setError(res.refused.map((r) => r.reason).join('; '));
            onDone();
        } catch (e) {
            setError(readError(e));
        } finally {
            setBusy(false);
        }
    };

    const columns = useMemo<DataSheetColumn<ApprovalLine>[]>(
        () => [
            {
                key: 'keep',
                header: '',
                width: 40,
                render: (_v, l): ReactElement | null =>
                    // A line asked for directly can be left out on its own; an input comes and goes with its document.
                    !result && l.instrumentIds.some((id) => askedSet.has(id)) ? (
                        <div onClick={(e) => e.stopPropagation()} role="presentation">
                            <Checkbox size="xs" checked onChange={() => dropLine(l)} title="Leave this line out; it stays proposed" />
                        </div>
                    ) : null,
            },
            { key: 'item', header: 'Line', width: 260, sticky: true, render: (_v, l): ReactElement => <Lines lines={[{ text: l.itemName ?? l.itemId, strong: true }, { text: '', sub: l.unit ?? '' }]} /> },
            { key: 'qty', header: 'Qty', width: 110, align: 'right', render: (_v, l): ReactElement => <Text size="sm" fw={700}>{n(l.qty, l.unit ? 1 : 0)}</Text> },
            {
                key: 'for',
                header: 'Why',
                width: 320,
                render: (_v, l): ReactElement => {
                    const runs = l.forRuns.slice(0, 2);
                    if (runs.length === 0) return <Lines lines={[{ text: l.forSkus.join(', '), sub: `${l.instrumentIds.length} line${l.instrumentIds.length === 1 ? '' : 's'} summed` }]} />;
                    return (
                        <Lines
                            lines={runs.map((r) => ({
                                text: `${r.sku} · dips ${whenLabel(r.dipDate, today)}${r.zeroDate ? ` · zero ${dayLabel(r.zeroDate)}` : ''}`,
                                late: Boolean(r.dipDate && r.dipDate <= today),
                                sub: `run ${dayLabel(r.runStart)} · ${r.inThisApproval ? 'in this approval' : r.runStatus === 'PROPOSED' ? 'run not approved yet' : `run ${r.runStatus.toLowerCase()}`}${l.forRuns.length > 2 ? ` · +${l.forRuns.length - 2} more` : ''}`,
                            }))}
                        />
                    );
                },
            },
            { key: 'placeBy', header: 'Earliest place by', width: 130, render: (_v, l): ReactElement => <Lines lines={[{ text: whenLabel(l.placeBy, today), late: l.placeBy <= today }]} /> },
            { key: 'lands', header: 'Lands by', width: 110, render: (_v, l): ReactElement => <Text size="sm">{dayLabel(l.landDate)}</Text> },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [today, result, askedSet],
    );

    if (!ids) return null;
    const groups = result ?? preview?.groups ?? [];
    const total = groups.reduce((s, g) => s + g.instrumentIds.length, 0);
    const title = result ? 'Approved' : 'Review before approving';
    const subtitle = result
        ? `${groups.length} document${groups.length === 1 ? '' : 's'} created as drafts · issue, send or push them from their own pages`
        : preview
          ? `${total} line${total === 1 ? '' : 's'} across ${groups.length} document${groups.length === 1 ? '' : 's'} · linked transfers and orders are included`
          : '';

    return (
        <ExplainWindow
            opened
            wide
            onClose={onClose}
            title={title}
            subtitle={subtitle}
            banner={
                result ? undefined : (
                    <Group gap="md" wrap="wrap" align="center">
                        <Text size="sm">One document per vendor, factory or lane</Text>
                        <SegmentedControl size="xs" value={groupBy} onChange={(v) => setGroupBy(v as ApprovalGroupBy)} data={[{ value: 'DAY', label: 'per day' }, { value: 'MONTH', label: 'per month' }]} />
                        <Text size="xs" c="dimmed">
                            Grouped lines are summed; the document is dated at the earliest place-by in the group.
                        </Text>
                    </Group>
                )
            }
            footer={
                <Group justify="space-between" p="sm">
                    <Text size="xs" c="dimmed">
                        {result ? 'Documents are drafts until issued; the plan counts them meanwhile.' : 'Approving a line approves its whole chain.'}
                    </Text>
                    <Group gap="xs">
                        <Button size="xs" variant="default" onClick={onClose}>
                            {result ? 'Done' : 'Cancel'}
                        </Button>
                        {!result && (
                            <Button size="xs" onClick={() => void confirm()} loading={busy} disabled={loading || !preview || groups.length === 0}>
                                Approve {total} {total === 1 ? 'line' : 'lines'}
                            </Button>
                        )}
                    </Group>
                </Group>
            }
        >
            <Stack gap="md" p="md">
                {error && (
                    <Alert color="red" withCloseButton onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {loading && !preview && <Loader size="sm" />}
                {preview && preview.refused.length > 0 && !result && (
                    <Alert color="orange" title="Not included">
                        {preview.refused.map((r) => r.reason).join(' · ')}
                    </Alert>
                )}
                {preview && preview.alreadyApproved.length > 0 && !result && (
                    <Alert color="teal" title={`${preview.alreadyApproved.length} input${preview.alreadyApproved.length === 1 ? '' : 's'} already approved`}>
                        <Text size="xs">{preview.alreadyApproved.map((a) => `${a.itemName ?? a.type} ${n(a.qty, 1)} for ${a.forSku} · ${a.status.toLowerCase()}${a.documentCode ? ` · ${a.documentCode}` : ''} · lands ${dayLabel(a.landDate)}`).join(' — ')}</Text>
                    </Alert>
                )}
                {dropped.length > 0 && !result && (
                    <Alert color="gray" title="Lines left out — they stay proposed">
                        <Stack gap={4}>
                            {dropped.map((l) => (
                                <Checkbox key={l.instrumentIds.join()} size="xs" checked={false} onChange={() => keepLine(l)} label={`${l.itemName ?? l.itemId} · ${n(l.qty, l.unit ? 1 : 0)}${l.forSkus.length ? ` · for ${l.forSkus.join(', ')}` : ''}`} />
                            ))}
                        </Stack>
                    </Alert>
                )}
                {leftOut.length > 0 && !result && (
                    <Alert color="orange" title="Left out of this approval">
                        <Stack gap={4}>
                            {leftOut.map((g) => {
                                const fed = [...new Set(g.lines.flatMap((l) => l.forRuns.filter((r) => r.inThisApproval).map((r) => `${r.sku} (starts ${dayLabel(r.runStart)}, this lands ${dayLabel(g.landDate)})`)))];
                                return (
                                    <Group key={g.key} gap="xs" wrap="wrap">
                                        <Checkbox size="xs" checked={false} onChange={() => bringBack(g)} label={`${KIND_LABEL[g.kind]} · ${g.partyName ?? g.partyId ?? '—'} · ${g.lines.length} line${g.lines.length === 1 ? '' : 's'}`} />
                                        {fed.length > 0 && (
                                            <Text size="xs" c="red">
                                                {fed.join('; ')} was sized on this landing; without it the run starts when the material does arrive.
                                            </Text>
                                        )}
                                    </Group>
                                );
                            })}
                        </Stack>
                    </Alert>
                )}
                {groups.map((g) => (
                    <Stack key={g.key} gap={6}>
                        <Group gap="sm" align="baseline" wrap="wrap">
                            {!result && !g.asked && <Checkbox size="xs" checked onChange={() => leaveOut(g)} title="Leave this input out; it stays proposed" />}
                            <Badge size="sm" variant="light" tt="none">
                                {KIND_LABEL[g.kind]}
                            </Badge>
                            {!g.asked && !result && (
                                <Text size="xs" c="dimmed">
                                    input of a run here
                                </Text>
                            )}
                            <Text size="sm" fw={700}>
                                {g.partyName ?? g.partyId ?? '—'}
                            </Text>
                            {g.kind !== 'STN' && g.toFacilityId && (
                                <Text size="xs" c="dimmed">
                                    to {g.toFacilityId}
                                </Text>
                            )}
                            <Text size="xs" c="dimmed">
                                {KIND_DATE[g.kind]} {whenLabel(g.orderDate, today)} · lands by {dayLabel(g.landDate)}
                            </Text>
                            {g.documentCode && (
                                <Badge size="sm" color="blue" variant="light" tt="none">
                                    {g.documentCode}
                                </Badge>
                            )}
                            {g.error && (
                                <Text size="xs" c="red">
                                    not created: {g.error}
                                </Text>
                            )}
                        </Group>
                        <DataSheet<ApprovalLine> data={g.lines} columns={columns} getRowKey={(l) => l.itemId} rowHeight={44} bare hideHeaderBar />
                    </Stack>
                ))}
                {preview && groups.length === 0 && !loading && (
                    <Text size="sm" c="dimmed">
                        Nothing left to approve here.
                    </Text>
                )}
            </Stack>
        </ExplainWindow>
    );
};

export default ApprovalSheet;
